/**
 * Data layer for `publisher-vs-publisher-comparison-widget`. Thin typed fetch wrappers over /api/v1/publisher-vs-publisher-comparison-widget.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PublisherVsPublisherComparisonWidgetRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPublisherVsPublisherComparisonWidgetResponse {
	count: number;
	items: PublisherVsPublisherComparisonWidgetRow[];
}

export interface CreatePublisherVsPublisherComparisonWidgetBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/publisher-vs-publisher-comparison-widget";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPublisherVsPublisherComparisonWidgetResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPublisherVsPublisherComparisonWidgetResponse>(res);
}

export async function create(body: CreatePublisherVsPublisherComparisonWidgetBody): Promise<PublisherVsPublisherComparisonWidgetRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PublisherVsPublisherComparisonWidgetRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const publisherVsPublisherComparisonWidgetQueryKeys = {
	all: ["publisher-vs-publisher-comparison-widget"] as const,
	list: () => [...publisherVsPublisherComparisonWidgetQueryKeys.all] as const,
	detail: (id: string) => [...publisherVsPublisherComparisonWidgetQueryKeys.all, id] as const,
};
